import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';

type AdminRole = 'admin' | 'operator' | 'client_viewer';

// Supabase localStorage key for this project
const SUPABASE_PROJECT_REF = 'obdggmcxrswxhpoozqcl';
const STORAGE_KEY = `sb-${SUPABASE_PROJECT_REF}-auth-token`;

type AdminAuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionChecked: boolean;
  role: AdminRole | null;
  isStaff: boolean;
  tenantId: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [initialSessionChecked, setInitialSessionChecked] = useState(false);

  const fetchUserRole = useCallback(async (userId: string) => {
    try {
      // Check advisory_user_roles table for admin/operator roles
      const { data, error } = await supabase
        .from('advisory_user_roles')
        .select('role, tenant_id')
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching user role:', error);
        return;
      }

      if (data && data.length > 0) {
        // Prioritize admin > operator > client_viewer
        const adminRole = data.find(r => r.role === 'admin');
        const operatorRole = data.find(r => r.role === 'operator');
        const clientRole = data.find(r => r.role === 'client_viewer');
        
        const primaryRole = adminRole || operatorRole || clientRole;
        if (primaryRole) {
          setRole(primaryRole.role as AdminRole);
          setTenantId(primaryRole.tenant_id);
        }
      }
    } catch (err) {
      console.error('Error in fetchUserRole:', err);
    }
  }, []);

  const refreshRole = useCallback(async () => {
    if (user?.id) {
      await fetchUserRole(user.id);
    }
  }, [user?.id, fetchUserRole]);

  useEffect(() => {
    let mounted = true;
    let initialSessionHandled = false;

    // STEP 1: Attempt to manually restore session from localStorage FIRST
    // This bypasses Supabase's unreliable initialization in new tabs
    const manuallyRestoreSession = async (): Promise<boolean> => {
      try {
        const storedSession = localStorage.getItem(STORAGE_KEY);
        if (!storedSession) {
          console.log('Admin auth: no stored session in localStorage');
          return false;
        }

        const parsed = JSON.parse(storedSession);
        if (!parsed.access_token || !parsed.refresh_token) {
          console.log('Admin auth: stored session missing tokens');
          return false;
        }

        console.log('Admin auth: found session in localStorage, manually restoring...');
        
        // Use setSession to manually restore the session
        const { data, error } = await supabase.auth.setSession({
          access_token: parsed.access_token,
          refresh_token: parsed.refresh_token,
        });
        
        if (error) {
          console.log('Admin auth: setSession failed:', error.message);
          return false;
        }

        if (data.session?.user) {
          console.log('Admin auth: session manually restored successfully');
          setSession(data.session);
          setUser(data.session.user);
          await fetchUserRole(data.session.user.id);
          if (mounted) {
            setInitialSessionChecked(true);
            setLoading(false);
            initialSessionHandled = true;
          }
          return true;
        }

        return false;
      } catch (err) {
        console.log('Admin auth: manual session restoration failed:', err);
        return false;
      }
    };

    // STEP 2: Set up auth state listener for ongoing changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('Admin auth onAuthStateChange:', event, { hasSession: !!session, initialSessionHandled });
        
        // Skip INITIAL_SESSION if we already handled it manually
        if (event === 'INITIAL_SESSION') {
          if (initialSessionHandled) {
            console.log('Admin auth: skipping INITIAL_SESSION (already handled manually)');
            return;
          }
          // If we get here with a session, use it; otherwise we already tried manual restoration
          if (session?.user) {
            console.log('Admin auth: INITIAL_SESSION has user');
            setSession(session);
            setUser(session.user);
            await fetchUserRole(session.user.id);
          }
          // Mark as checked regardless - we've done everything we can
          if (mounted && !initialSessionHandled) {
            setInitialSessionChecked(true);
            setLoading(false);
          }
        } else if (event === 'SIGNED_IN') {
          console.log('Admin auth: SIGNED_IN event');
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            await fetchUserRole(session.user.id);
          }
          initialSessionHandled = true;
          if (mounted) {
            setInitialSessionChecked(true);
            setLoading(false);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('Admin auth: SIGNED_OUT event');
          setSession(null);
          setUser(null);
          setRole(null);
          setTenantId(null);
          if (mounted) {
            setInitialSessionChecked(true);
            setLoading(false);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('Admin auth: TOKEN_REFRESHED event');
          setSession(session);
          setUser(session?.user ?? null);
        }
      }
    );

    // STEP 3: Initialize auth - try manual restoration first
    const initializeAuth = async () => {
      const restored = await manuallyRestoreSession();
      console.log('Admin auth: manual restoration result:', restored);
      
      // If manual restoration succeeded, we're done
      // If not, the onAuthStateChange INITIAL_SESSION will handle it
      // (or mark as checked with no user if truly not logged in)
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserRole]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setRole(null);
    setTenantId(null);
  };

  // Compute derived state
  const isStaff = role === 'admin' || role === 'operator';
  
  console.log('Admin auth state:', { 
    hasUser: !!user, 
    role, 
    initialSessionChecked
  });

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        session,
        loading,
        role,
        isStaff,
        tenantId,
        signIn,
        signOut,
        refreshRole,
        sessionChecked: initialSessionChecked,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
